"use client"

// components/ui/fullscreen-calendar.tsx
//
// THE CALENDAR GRID — Paper node LA2-0 ("Calendar · v2"), ported.
//
// Source of truth: Paper file "Dashboard", artboard "Calendar · v2" (LA2-0),
// specifically its "Calendar view" card (toolbar + Monday-first month grid +
// a right-hand conference rail). Every colour, size and shadow below is the
// artboard's own computed value read through `get_jsx` / `get_computed_styles`
// -- nothing here was measured off a screenshot.
//
// FE-READOUTS-1 Phase A recycles this component rather than rebuilding it
// (founder note 2026-08-24: the port is a restyle + rail + data, not a new
// calendar engine). What Phase A adds:
//   - a CONTROLLED month (`month` + `onMonthChange`), backward-compatible:
//     omit both and the component stays uncontrolled exactly as before. The
//     conference rail's jump-to-month (Phase B) drives it.
//   - LA2-0's material toolbar in place of the old neumorphic header.
//   - weekend-receded columns (#F3F6F9 ground on Sat/Sun).
//   - the halo on the next readout (`highlightDay`): a drug-tinted chip border
//     plus a two-stop ring around the chip's dot.
//   - a `rail` SLOT. LA2-0's grid is 891 of the split's 1097px, i.e. its width
//     DEPENDS on the rail being there, so Phase A reserves the column and
//     Phase B fills it -- same posture as `.bandArt` on the Changes band.

import * as React from "react"
import {
  add,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isEqual,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfToday,
  startOfWeek,
} from "date-fns"
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import styles from "@/components/ui/fullscreen-calendar.module.css"

export type FullScreenCalendarEvent = {
  id: number
  name: string
  time: string
  datetime: string
  description?: string
  nctId?: string
  /** Drug accent (LA2-0's chip dot + halo). Neutral dot when absent. */
  accent?: string
  /** The drug's sphere avatar URL (founder round 2) — replaces the colour
   *  dot when present; the dot remains the drugless fallback. */
  avatar?: string
}

export type FullScreenCalendarDay = {
  day: Date
  events: FullScreenCalendarEvent[]
}

/**
 * A multi-day span drawn across the top of the grid's week rows (founder
 * round 2026-08-26) — Teams-style all-day bands. The Calendar feeds it the
 * watch's dated conference editions; `color` is the society's brand hex
 * (conferences.brand_hex via the rail payload), null for a neutral band.
 */
export type CalendarBand = {
  key: string
  label: string
  color: string | null
  /** The gradient partner (secondary_hex); null derives a darkened partner
   *  from `color` in CSS. Ignored when `color` is null. */
  color2?: string | null
  start: Date
  end: Date
  /** Why this band is on the grid (round 3: bands are candidate meetings
   *  only) — shown as the segment's title. */
  hint?: string
}

/** Band lane geometry + the per-week cap that protects the readout chips. */
const BAND_H = 15
const BAND_GAP = 2
const MAX_BANDS_PER_WEEK = 3

export type FullScreenCalendarProps = {
  data: FullScreenCalendarDay[]
  /** Month to open on when uncontrolled (defaults to today). */
  initialDate?: Date
  /**
   * CONTROLLED month. Pass it (with `onMonthChange`) to drive the grid from
   * outside -- the conference rail's jump-to-month. Omit it and the component
   * owns its own month, which is what every pre-Phase-A caller does.
   */
  month?: Date
  onMonthChange?: (month: Date) => void
  /**
   * The WATCH-WIDE half of the toolbar sub-line, e.g. "220 upcoming on your
   * watch \u00b7 84 without a day". The component prepends the count of what is
   * actually ON the displayed grid, so the sub-line never lets a whole-watch
   * number sit under a month title as if it described that month.
   */
  watchSummary?: string
  /** The day carrying the next readout -- takes LA2-0's halo. */
  highlightDay?: Date
  /** Right-hand rail. Reserves LA2-0's 206px column when present. */
  rail?: React.ReactNode
  /** Mobile-only content placed between the compact grid and conference rail. */
  afterGrid?: React.ReactNode
  /** Conference bands across the week rows (desktop grid only). */
  bands?: CalendarBand[]
  /** Fired when a day is chosen — use for a day-detail modal. */
  onDaySelect?: (
    day: Date,
    events: FullScreenCalendarEvent[],
    trigger: HTMLButtonElement,
  ) => void
  /** Selection changes without the activation semantics of `onDaySelect`. */
  onSelectedDayChange?: (day: Date) => void
  /**
   * Fired when a conference band is clicked (FE-READOUTS-1 Phase D — the
   * Conference card). When present, each band renders as a real BUTTON whose
   * accessible name is the band's honesty string; without it the band stays
   * the round-4 role="img" span. Either way the round-4 pointer-events
   * treatment holds: the lane is click-transparent, the band itself is not,
   * and day-cell clicks land below via the spacer.
   */
  onBandSelect?: (band: CalendarBand) => void
}

/** LA2-0 is Monday-first, which is what puts the two receded weekend columns
 *  together at the right edge instead of splitting them across the grid. */
const WEEK_STARTS_ON = 1 as const
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const

function isWeekend(day: Date): boolean {
  const d = day.getDay()
  return d === 0 || d === 6
}

export function FullScreenCalendar({
  data,
  initialDate,
  month,
  onMonthChange,
  watchSummary,
  highlightDay,
  rail,
  afterGrid,
  bands,
  onDaySelect,
  onSelectedDayChange,
  onBandSelect,
}: FullScreenCalendarProps) {
  const today = startOfToday()
  const anchor = initialDate ?? today
  const [selectedDay, setSelectedDay] = React.useState(anchor)
  const [focusedDay, setFocusedDay] = React.useState(anchor)
  const [ownMonth, setOwnMonth] = React.useState(() => startOfMonth(anchor))
  const rootRef = React.useRef<HTMLDivElement>(null)
  const focusOnlyMonthChange = React.useRef(false)

  // Controlled when `month` is supplied, uncontrolled otherwise. `ownMonth` is
  // NOT kept in step while controlled -- it is only the fallback, and a caller
  // that hands control over mid-life would drop back to whatever month the
  // component last owned. No caller does that today.
  const firstDayCurrentMonth = month ? startOfMonth(month) : ownMonth
  const firstDayCurrentMonthMs = firstDayCurrentMonth.getTime()

  React.useEffect(() => {
    onSelectedDayChange?.(selectedDay)
  }, [onSelectedDayChange, selectedDay])

  React.useEffect(() => {
    if (!isSameMonth(selectedDay, firstDayCurrentMonth)) {
      if (focusOnlyMonthChange.current) {
        focusOnlyMonthChange.current = false
        return
      }
      setSelectedDay(firstDayCurrentMonth)
      setFocusedDay(firstDayCurrentMonth)
    }
  }, [firstDayCurrentMonthMs])

  const goToMonth = React.useCallback(
    (next: Date) => {
      const first = startOfMonth(next)
      if (month === undefined) setOwnMonth(first)
      onMonthChange?.(first)
    },
    [month, onMonthChange],
  )

  const days = eachDayOfInterval({
    start: startOfWeek(firstDayCurrentMonth, { weekStartsOn: WEEK_STARTS_ON }),
    end: endOfWeek(endOfMonth(firstDayCurrentMonth), {
      weekStartsOn: WEEK_STARTS_ON,
    }),
  })

  /* Week rows for the desktop grid — each carries its own band lane. */
  const weeks: Date[][] = []
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7))

  /** The bands overlapping one week row, clamped to its columns. Only spans
   *  overlapping the VISIBLE grid ever reach the DOM; a span crossing the
   *  week edge keeps a squared edge (Teams' own continuation grammar). */
  function segmentsFor(week: Date[]): {
    band: CalendarBand
    startCol: number
    span: number
    contLeft: boolean
    contRight: boolean
  }[] {
    if (!bands || bands.length === 0) return []
    const first = week[0]
    const segs: {
      band: CalendarBand
      startCol: number
      span: number
      contLeft: boolean
      contRight: boolean
    }[] = []
    for (const b of bands) {
      const s = differenceInCalendarDays(b.start, first)
      const e = differenceInCalendarDays(b.end, first)
      if (e < 0 || s > 6 || e < s) continue
      const startCol = Math.max(0, s)
      const endCol = Math.min(6, e)
      segs.push({
        band: b,
        startCol,
        span: endCol - startCol + 1,
        contLeft: s < 0,
        contRight: e > 6,
      })
    }
    segs.sort(
      (a, b2) =>
        a.startCol - b2.startCol ||
        b2.span - a.span ||
        a.band.label.localeCompare(b2.band.label),
    )
    return segs
  }

  /* THE HONEST HALF OF THE SUB-LINE. Counted from `data` -- the same buckets
     the grid paints -- restricted to the month in the title. `data` only ever
     holds day-precise entries (the page drops month/year-precision ones rather
     than invent a day), so this is literally "chips you can see", and the
     whole-watch total sits behind its own words in `watchSummary`. */
  const visibleThisMonth = data.reduce(
    (n, bucket) =>
      isSameMonth(bucket.day, firstDayCurrentMonth)
        ? n + bucket.events.length
        : n,
    0,
  )
  const subLine = [
    `${visibleThisMonth.toLocaleString("en-GB")} on this month's grid`,
    watchSummary,
  ]
    .filter(Boolean)
    .join(" \u00b7 ")

  function eventsFor(day: Date): FullScreenCalendarEvent[] {
    return data
      .filter((bucket) => isSameDay(bucket.day, day))
      .flatMap((bucket) => bucket.events)
  }

  function selectDay(day: Date, trigger: HTMLButtonElement) {
    setSelectedDay(day)
    setFocusedDay(day)
    const events = eventsFor(day)
    onDaySelect?.(day, events, trigger)
  }

  function moveKeyboardSelection(day: Date, delta: number) {
    const next = add(day, { days: delta })
    setFocusedDay(next)
    if (!isSameMonth(next, firstDayCurrentMonth)) {
      focusOnlyMonthChange.current = true
      goToMonth(next)
    }
    window.requestAnimationFrame(() => {
      const candidates = rootRef.current?.querySelectorAll<HTMLButtonElement>(
        `[data-calendar-day="${format(next, "yyyy-MM-dd")}"]`,
      )
      const visible = candidates
        ? [...candidates].find((button) => button.getClientRects().length > 0 && !button.disabled)
        : undefined
      visible?.focus()
    })
  }

  function dayKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, day: Date) {
    const delta = event.key === "ArrowLeft" ? -1
      : event.key === "ArrowRight" ? 1
        : event.key === "ArrowUp" ? -7
          : event.key === "ArrowDown" ? 7
            : null
    if (delta !== null) {
      event.preventDefault()
      moveKeyboardSelection(day, delta)
      return
    }
    if (event.key === "Home" || event.key === "End") {
      event.preventDefault()
      const weekday = (day.getDay() + 6) % 7
      moveKeyboardSelection(day, event.key === "Home" ? -weekday : 6 - weekday)
    }
  }

  /** LA2-0's day chip: dot + name + the event's own meta line. On a day that
   *  carries more than one readout the overflow count is a THIRD line rather
   *  than a replacement -- the artboard swaps the meta out, but the chip on the
   *  next readout is the one wearing the halo, and dropping its
   *  "Phase 1 \u00b7 estimate" label is exactly the estimate-grammar failure this
   *  page exists to fix. */
  function renderChip(
    day: Date,
    events: FullScreenCalendarEvent[],
  ): React.ReactNode {
    const event = events[0]
    if (!event) return null
    const halo = highlightDay ? isSameDay(day, highlightDay) : false
    const overflow = events.length - 1
    return (
      <div
        key={event.id}
        className={cn(styles.eventCard, halo && styles.eventCardNext)}
        style={
          event.accent
            ? ({ "--evt-accent": event.accent } as React.CSSProperties)
            : undefined
        }
      >
        <div className={styles.eventTop}>
          {/* Founder round 2: the real sphere leads the chip; the colour dot
              is only the drugless fallback. The halo ring wears the same
              treatment on either. */}
          {event.avatar ? (
            <span
              className={styles.eventAvatar}
              style={{ backgroundImage: `url("${event.avatar}")` }}
              aria-hidden
            />
          ) : (
            <span
              className={cn(
                styles.eventDot,
                event.accent && styles.eventDotHued,
              )}
              aria-hidden
            />
          )}
          <p className={styles.eventName}>{event.name}</p>
        </div>
        <p className={styles.eventMeta}>{event.time}</p>
        {overflow > 0 ? (
          <p className={styles.eventMore}>+{overflow} more</p>
        ) : null}
      </div>
    )
  }

  const grid = (
    <div className={styles.gridColumn}>
      <div className={styles.weekHead} aria-hidden>
        {WEEKDAYS.map((d) => (
          <div key={d} className={styles.weekDay}>
            {d}
          </div>
        ))}
      </div>

      {/* Desktop — WEEK ROWS rather than one flat grid (founder round
          2026-08-26), so each week can carry its Teams-style conference band
          lane. With no bands the rows lay out exactly as the flat grid did
          (equal-flex rows, same 104px floor, same hairlines). */}
      <div className={styles.daysDesktop}>
        {weeks.map((week, w) => {
          const segs = segmentsFor(week)
          const visible = segs.slice(0, MAX_BANDS_PER_WEEK)
          const extra = segs.length - visible.length
          /* The chips clear the lane via a spacer inside each cell; the +N
             notice takes its own thin row when the cap bites. */
          const laneH =
            visible.length > 0
              ? visible.length * (BAND_H + BAND_GAP) +
                (extra > 0 ? 12 : 0) +
                4
              : 0
          return (
            <div key={week[0].toISOString()} className={styles.weekRow}>
              {visible.length > 0 ? (
                /* NOT aria-hidden (round 4): the bands carry the honesty
                   string as their accessible name, so AT hears why each
                   meeting is on the grid. The lane stays click-transparent;
                   the band spans themselves take pointer events back for
                   the hover tooltip. */
                <div className={styles.bandLane}>
                  {visible.map((s, i) => {
                    const insetL = s.contLeft ? 0 : 3
                    const insetR = s.contRight ? 0 : 3
                    const bandClass = cn(
                      styles.band,
                      !s.band.color && styles.bandNeutral,
                      s.contLeft && styles.bandContLeft,
                      s.contRight && styles.bandContRight,
                      onBandSelect && styles.bandClickable,
                    )
                    const bandStyle = {
                      left: `calc(${(s.startCol / 7) * 100}% + ${insetL}px)`,
                      width: `calc(${(s.span / 7) * 100}% - ${insetL + insetR}px)`,
                      top: i * (BAND_H + BAND_GAP),
                      ...(s.band.color
                        ? { "--band-brand": s.band.color }
                        : null),
                      ...(s.band.color && s.band.color2
                        ? { "--band-brand2": s.band.color2 }
                        : null),
                    } as React.CSSProperties
                    /* Phase D: with a card handler the band is a REAL
                       button (promoted from round 4's role="img" span
                       because the band now DOES something). Its name is
                       the honesty string PLUS the action (review round):
                       the in-window count is the band's own fact, and the
                       card it opens uses the wider named asymmetric rule
                       (four months before to two weeks after) —
                       so the name states the band fact and offers the
                       action without promising the card's count. Without
                       a handler the round-4 span stands unchanged. */
                    return onBandSelect ? (
                      <button
                        key={s.band.key}
                        type="button"
                        aria-label={`${s.band.hint ?? s.band.label} · view candidate readouts`}
                        className={bandClass}
                        style={bandStyle}
                        title={`${s.band.hint ?? s.band.label} · view candidate readouts`}
                        onClick={() => onBandSelect(s.band)}
                      >
                        {s.band.label}
                      </button>
                    ) : (
                      <span
                        key={s.band.key}
                        role="img"
                        aria-label={s.band.hint ?? s.band.label}
                        className={bandClass}
                        style={bandStyle}
                        title={s.band.hint ?? s.band.label}
                      >
                        {s.band.label}
                      </span>
                    )
                  })}
                  {extra > 0 ? (
                    <span
                      className={styles.bandMore}
                      aria-hidden
                      style={{
                        top: visible.length * (BAND_H + BAND_GAP),
                      }}
                    >
                      +{extra} more
                    </span>
                  ) : null}
                </div>
              ) : null}
              {week.map((day, di) => {
                const events = eventsFor(day)
                const selected = isEqual(day, selectedDay)
                const outside = !isSameMonth(day, firstDayCurrentMonth)
                return (
                  <button
                    key={day.toISOString()}
                    type="button"
                    onClick={(event) => selectDay(day, event.currentTarget)}
                    onKeyDown={(event) => dayKeyDown(event, day)}
                    data-calendar-day={format(day, "yyyy-MM-dd")}
                    aria-label={`${format(day, "EEEE d MMMM yyyy")}${events.length ? `: ${events.map((item) => item.description ?? item.name).join("; ")}` : ": no readouts"}`}
                    aria-pressed={selected}
                    aria-current={isToday(day) ? "date" : undefined}
                    tabIndex={isEqual(day, focusedDay) ? 0 : -1}
                    className={cn(
                      styles.day,
                      isWeekend(day) && styles.dayWeekend,
                      outside && styles.dayOutside,
                      di === 6 && styles.dayLastColumn,
                      w === weeks.length - 1 && styles.dayLastRow,
                      selected && styles.daySelected,
                    )}
                  >
                    <span className={styles.dayHead}>
                      <span
                        className={cn(
                          styles.dayNum,
                          isToday(day) && styles.dayNumToday,
                        )}
                      >
                        <time dateTime={format(day, "yyyy-MM-dd")}>
                          {format(day, "d")}
                        </time>
                      </span>
                      {outside ? (
                        <span className={styles.dayMonthTag} aria-hidden>
                          {format(day, "MMM")}
                        </span>
                      ) : null}
                    </span>
                    {laneH > 0 ? (
                      <span
                        className={styles.bandSpacer}
                        style={{ height: laneH }}
                        aria-hidden
                      />
                    ) : null}
                    {renderChip(day, events)}
                  </button>
                )
              })}
            </div>
          )
        })}
      </div>

      {/* Mobile */}
      <div className={styles.daysMobile}>
        {days.map((day) => {
          const events = eventsFor(day)
          const selected = isEqual(day, selectedDay)
          const outside = !isSameMonth(day, firstDayCurrentMonth)
          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={(event) => selectDay(day, event.currentTarget)}
              onKeyDown={(event) => dayKeyDown(event, day)}
              data-calendar-day={format(day, "yyyy-MM-dd")}
              aria-label={`${format(day, "EEEE d MMMM yyyy")}${events.length ? `: ${events.map((item) => item.description ?? item.name).join("; ")}` : ": no readouts"}`}
              aria-pressed={selected}
              aria-current={isToday(day) ? "date" : undefined}
              tabIndex={isEqual(day, focusedDay) ? 0 : -1}
              className={cn(
                styles.day,
                styles.dayMobile,
                isWeekend(day) && styles.dayWeekend,
                outside && styles.dayOutside,
                selected && styles.daySelected,
              )}
            >
              <span
                className={cn(styles.dayNum, isToday(day) && styles.dayNumToday)}
              >
                <time dateTime={format(day, "yyyy-MM-dd")}>
                  {format(day, "d")}
                </time>
              </span>
              {events.length > 0 ? (
                <span className={styles.dots} aria-hidden>
                  {events.slice(0, 3).map((event) => (
                    <span
                      key={event.id}
                      className={styles.dot}
                      style={
                        event.avatar
                          ? {
                              backgroundImage: `url("${event.avatar}")`,
                              backgroundSize: "cover",
                              backgroundPosition: "50%",
                            }
                          : event.accent
                            ? { background: event.accent }
                            : undefined
                      }
                    />
                  ))}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>
    </div>
  )

  return (
    <div className={styles.root} ref={rootRef}>
      {/* LA2-0's material toolbar: the dark hero plate with the month title on
          the left and the month nav on the right. */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarTitleBlock}>
          <p className={styles.toolbarMonth}>
            {format(firstDayCurrentMonth, "MMMM yyyy")}
          </p>
          <p className={styles.toolbarSub}>{subLine}</p>
        </div>

        <div className={styles.nav} role="group" aria-label="Month navigation">
          <button
            type="button"
            onClick={() => goToMonth(add(firstDayCurrentMonth, { months: -1 }))}
            className={styles.navBtn}
            aria-label="Previous month"
          >
            <ChevronLeftIcon size={16} strokeWidth={2} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => {
              goToMonth(today)
              setSelectedDay(today)
              setFocusedDay(today)
            }}
            className={cn(styles.navBtn, styles.navToday)}
            aria-label={`Go to ${format(today, "MMMM yyyy")}`}
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => goToMonth(add(firstDayCurrentMonth, { months: 1 }))}
            className={styles.navBtn}
            aria-label="Next month"
          >
            <ChevronRightIcon size={16} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* LA2-0's "Calendar split": the grid, then the rail column. */}
      <div className={styles.split}>
        {grid}
        {afterGrid !== undefined ? (
          <div className={styles.afterGrid}>{afterGrid}</div>
        ) : null}
        {rail !== undefined ? (
          <aside className={styles.railColumn}>{rail}</aside>
        ) : null}
      </div>
    </div>
  )
}
